import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Stethoscope, Plus, Trash2, Camera, Loader2, X, FileText, Check, ChevronLeft, ChevronRight, Car,
    Pencil, Calendar, Wrench,
} from 'lucide-react';
import { Modal } from '../../../../components/ui/Modal';
import { refreshMotHistoryWithin } from '../../../../services/vrmService';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { useOrgSettings } from '../../../../hooks/useOrgSettings';
import { CustomerTypeahead, type CustomerSearchResult } from '../components/CustomerTypeahead';
import { VRMLookup, type FinalVehicleData, type LinkedCustomerData } from '../../../../components/vehicles/VRMLookup';
import { reportError } from '../../../../lib/errors';
import { motOdometerOffer } from '../../../../lib/vehicle';
import { LineItemsEditor, type EditableLineItem } from '../../../../components/ui/LineItemsEditor';

interface VehicleRow {
    id: string;
    vrm: string;
    make: string | null;
    model: string | null;
}

/**
 * TTL for `diagnostic-photos` signed URLs, in seconds (7 days).
 *
 * The bucket is private: these are customer vehicle photographs, which are
 * personal data under UK GDPR and routinely show number plates. A public URL
 * would serve them unauthenticated and bypass the bucket's org-scoped RLS
 * policies, so every read is signed.
 *
 * KNOWN LIMITATION: `Finding.photos` stores the signed URL itself, so a photo
 * attached to a saved report stops loading once this TTL lapses. The correct
 * end-state is to persist the object *path* and sign on render; that needs the
 * two render sites below to resolve paths asynchronously, so it is deliberately
 * left as a follow-up rather than an untested refactor of this component.
 */
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 7;

/**
 * Hardcoded diagnostic taxonomy — the standard system checklist for the
 * automotive vertical. Modelled on WalkaroundCapture's zone list. Deliberately
 * hardcoded for now; if real-world feedback asks for it, this becomes an
 * org-editable list (same move the app made for vertical jargon).
 */
const SYSTEMS = [
    'Engine',
    'Brakes',
    'Suspension & Steering',
    'Tyres & Wheels',
    'Exhaust & Emissions',
    'Electrical & Battery',
    'Cooling System',
    'Transmission & Clutch',
    'Fuel System',
    'Lights & Visibility',
    'Body & Interior',
    'Other',
] as const;

type Severity = 'critical' | 'advisory' | 'monitor';

// Every input in this modal gets one of these above it. Placeholders were
// doing the labelling here once; when they were removed the fields went bare.
const dFieldLabelCls =
    'block text-fd-3xs uppercase tracking-wider text-fg-3 font-display font-semibold';

const SEVERITY_META: Record<Severity, { label: string; className: string; dot: string }> = {
    critical: {
        label: 'Critical',
        className: 'bg-danger/10 text-danger border-danger/30',
        dot: 'bg-danger-fill',
    },
    advisory: {
        label: 'Advisory',
        className: 'bg-gold-fill/10 text-gold border-gold/30',
        dot: 'bg-gold-fill',
    },
    monitor: {
        label: 'Monitor',
        className: 'bg-success/10 text-success border-success/30',
        dot: 'bg-success-fill',
    },
};

export interface Finding {
    id: string;
    system: string;
    /** The FAULT — "front brake pads worn to 2mm". Never a line item. */
    title: string;
    severity: Severity;
    notes: string;
    photos: string[];
    /**
     * The PART needed to fix it — "Front brake pads (set)". Distinct from
     * `title` on purpose: the quote's part line used to be the fault title, so
     * a diagnostic that said "knocking on left turn" produced a quote whose
     * Parts column read "knocking on left turn". Blank falls back to `title`,
     * which is the old behaviour, so existing reports are unaffected.
     */
    partDescription?: string;
    price: number; // what the part COST, ex VAT — markup is applied on the quote
    labourHours: number;
    /**
     * ISO date this finding should come back as a follow-up.
     *
     * Optional, and pre-filled — advisory in 3 months, monitor in 6 — so a
     * mechanic never has to type it. Only advisory and monitor findings carry
     * one: a critical fault is a job, not something to be reminded about later.
     * fn_get_followups falls back to the same +3/+6 defaults, so every report
     * written before this field existed still surfaces.
     */
    remindAt?: string;
}

export interface DiagnosticContext {
    reportId?: string;
    customerId?: string;
    vehicleId?: string;
    appointmentId?: string;
    customerName?: string;
    vehicleVrm?: string;
    vehicleDesc?: string;
    /** Existing findings when editing a saved report. */
    findings?: Finding[];
    lineItems?: EditableLineItem[];
    summary?: string;
    /** Status + date of a saved report, shown in the read-only view. */
    status?: 'in_progress' | 'complete' | 'quoted';
    createdAt?: string;
    quoteId?: string | null;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSaved?: () => void;
    context?: DiagnosticContext;
}

const uid = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

/** Advisory comes back in 3 months, monitor in 6. Matches fn_get_followups. */
const defaultRemindAt = (severity: Severity): string => {
    const d = new Date();
    d.setMonth(d.getMonth() + (severity === 'monitor' ? 6 : 3));
    return d.toISOString().slice(0, 10);
};

const blankFinding = (): Finding => ({
    id: uid(),
    system: SYSTEMS[0],
    title: '',
    severity: 'advisory',
    notes: '',
    photos: [],
    price: 0,
    labourHours: 0,
    partDescription: '',
    remindAt: defaultRemindAt('advisory'),
});

/**
 * Diagnostic capture + report viewer. Three entry points share this one modal:
 *   1. From an appointment  → context pre-fills the customer/vehicle.
 *   2. Walk-in (dashboard)  → the user searches for a customer + picks a vehicle.
 *   3. An existing report   → opens read-only, with an Edit button.
 *
 * The mechanic logs findings system-by-system (severity + photos + optional
 * suggested price/labour), then either saves the report for the vehicle's
 * history or turns it straight into a draft quote in one tap.
 */
export const DiagnosticModal = ({ isOpen, onClose, onSaved, context }: Props) => {
    const { activeOrgId } = useVertical();
    const { settings: orgSettings } = useOrgSettings();
    const navigate = useNavigate();

    const [reportId, setReportId] = useState<string>(() => context?.reportId || uid());
    // A saved report opens read-only so the mechanic can read back what was
    // found; editing is one tap away.
    const [mode, setMode] = useState<'view' | 'edit'>(context?.reportId ? 'view' : 'edit');
    const [customerId, setCustomerId] = useState<string | undefined>(context?.customerId);
    const [customerName, setCustomerName] = useState<string>(context?.customerName || '');
    const [vehicleVrm, setVehicleVrm] = useState<string>(context?.vehicleVrm || '');
    const [vehicleDesc, setVehicleDesc] = useState<string>(context?.vehicleDesc || '');
    const [vehicleId, setVehicleId] = useState<string | undefined>(context?.vehicleId);

    // Picker step — once a vehicle is locked in (from context, an existing pick,
    // or a freshly-created customer/vehicle), we move on to findings.
    const [pickerDone, setPickerDone] = useState<boolean>(
        Boolean(context?.reportId || context?.customerName || context?.vehicleVrm),
    );
    const [pickerCustomer, setPickerCustomer] = useState<{ id: string; name: string } | null>(null);
    const [customerVehicles, setCustomerVehicles] = useState<VehicleRow[]>([]);
    const [savingCustomer, setSavingCustomer] = useState(false);

    // New customer / new vehicle form fields.
    //
    // The reg fields are filled by the shared <VRMLookup>, the same component the
    // job, quote, booking and vehicle-manager flows use. This screen used to have
    // a bare `vrm / make / model` text form with NO DVLA lookup at all — the only
    // place in the app where typing a reg did not fetch the vehicle — and it
    // stored nothing beyond those three fields, so colour, year, engine, fuel type
    // and MOT expiry had to be retyped later on the vehicle card.
    const [form, setForm] = useState({
        name: '', phone: '', email: '',
        vrm: '', make: '', model: '',
        year: '', engine: '', mileage: '', colour: '', fuel_type: '', mot_expiry: '',
    });
    const setF = (patch: Partial<typeof form>) => setForm((p) => ({ ...p, ...patch }));
    const blankForm = () => ({
        name: '', phone: '', email: '',
        vrm: '', make: '', model: '',
        year: '', engine: '', mileage: '', colour: '', fuel_type: '', mot_expiry: '',
    });
    // Whether the reg step is done, so the picker can swap the lookup for a
    // compact summary rather than leaving a large panel open above the fields.
    const [vehicleConfirmed, setVehicleConfirmed] = useState(false);

    const confirmedLookup = useRef<FinalVehicleData | null>(null);
    const handleVehicleConfirmed = (vehicle: FinalVehicleData) => {
        confirmedLookup.current = vehicle;
        setF({
            vrm: vehicle.vrm || '',
            make: vehicle.make || '',
            model: vehicle.model || '',
            year: String(vehicle.year || vehicle.yearOfManufacture || ''),
            engine: vehicle.engine || (vehicle.engineCapacity ? String(vehicle.engineCapacity) : ''),
            mileage: vehicle.mileage ? String(vehicle.mileage) : '',
            colour: vehicle.colour || '',
            fuel_type: vehicle.fuel_type || vehicle.fuelType || '',
            mot_expiry: vehicle.motExpiryDate ? String(vehicle.motExpiryDate).slice(0, 10) : '',
        });
        setVehicleConfirmed(true);
    };

    // Lookup failed and the user chose to type it in — keep the reg they entered.
    const handleSkipToManual = (vrm: string) => {
        setF({ vrm: vrm.toUpperCase() });
        setVehicleConfirmed(true);
    };

    // An existing customer found by their reg. The picker is mid-flow here, so
    // adopt them rather than making the user search for a customer the lookup has
    // already identified.
    const handleCustomerFound = (customer: LinkedCustomerData) => {
        setF({ name: customer.full_name, phone: customer.phone || '', email: customer.email || '' });
        setPickerCustomer({ id: customer.id, name: customer.full_name });
        const vehicle = confirmedLookup.current;
        if (vehicle?.id) {
            setCustomerId(customer.id);
            setCustomerName(customer.full_name);
            setVehicleId(vehicle.id);
            setVehicleVrm(vehicle.vrm);
            setVehicleDesc([vehicle.make, vehicle.model].filter(Boolean).join(' '));
            setPickerDone(true);
        }
    };

    const [findings, setFindings] = useState<Finding[]>(context?.findings?.length ? context.findings : [blankFinding()]);
    // Parts and labour for the remedy, in the SAME shape the quote and the job
    // use. Findings say what is wrong; these say what it takes to fix it. Keeping
    // them apart is what stops a fault description ending up in the Parts column
    // of a quote.
    const [lineItems, setLineItems] = useState<EditableLineItem[]>(context?.lineItems ?? []);
    const [summary, setSummary] = useState<string>(context?.summary || '');
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadingId, setUploadingId] = useState<string | null>(null);

    const validFindings = findings.filter((f) => f.title.trim());
    const labourRate = orgSettings?.default_labor_rate || 0;

    // Parts pricing works the same way here as it does on a job card and in the
    // quote builder: the mechanic types what the part COST them, and the org's
    // default markup produces what the customer pays. Findings used to take the
    // typed figure as the customer price, so the same part was quoted at cost
    // from a diagnostic and at cost + markup from a job — margin quietly lost,
    // and two screens disagreeing about what the number in the box means.
    const partsMarkup = orgSettings?.default_parts_markup || 0;
    const sellPrice = (cost: number) => Number((cost * (1 + partsMarkup / 100)).toFixed(2));

    // The modal is mounted once by its parent and reused for every report, so
    // state has to be (re)hydrated from `context` each time it opens — without
    // this, opening a saved report showed the previous report's data (or a
    // blank form) and saved under the wrong id. Guarded by a ref so a parent
    // re-render mid-edit can't wipe what the mechanic is typing.
    const hydratedFor = useRef<string | null>(null);
    useEffect(() => {
        if (!isOpen) {
            hydratedFor.current = null;
            return;
        }
        const key = context?.reportId ?? 'new';
        if (hydratedFor.current === key) return;
        hydratedFor.current = key;

        setReportId(context?.reportId || uid());
        setMode(context?.reportId ? 'view' : 'edit');
        setCustomerId(context?.customerId);
        setCustomerName(context?.customerName || '');
        setVehicleId(context?.vehicleId);
        setVehicleVrm(context?.vehicleVrm || '');
        setVehicleDesc(context?.vehicleDesc || '');
        setFindings(context?.findings?.length ? context.findings : [blankFinding()]);
        setLineItems(context?.lineItems ?? []);
        setSummary(context?.summary || '');
        setPickerDone(Boolean(context?.reportId || context?.customerName || context?.vehicleVrm));
        setPickerCustomer(null);
        setForm(blankForm()); setVehicleConfirmed(false);
        confirmedLookup.current = null;
        setError(null);
    }, [isOpen, context]);

    // When an existing customer is selected, load their vehicles (with ids, so
    // the report links to the real vehicle record for history).
    useEffect(() => {
        if (!activeOrgId || !pickerCustomer) {
            setCustomerVehicles([]);
            return;
        }
        supabase
            .from('vehicles')
            .select('id, vrm, make, model')
            .eq('org_id', activeOrgId)
            .eq('owner_id', pickerCustomer.id)
            .order('created_at', { ascending: false })
            .then(({ data }) => setCustomerVehicles((data as VehicleRow[]) ?? []));
    }, [activeOrgId, pickerCustomer]);

    const updateFinding = (id: string, patch: Partial<Finding>) =>
        setFindings((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

    const removeFinding = (id: string) => setFindings((prev) => prev.filter((f) => f.id !== id));

    const addLineItem = (type: 'part' | 'labour') =>
        setLineItems((prev) => [...prev, {
            id: uid(), type, description: '', quantity: type === 'labour' ? 1 : 1,
            unitPrice: type === 'labour' ? labourRate : 0, costPrice: 0,
        }]);
    const updateLineItem = (id: string, patch: Partial<EditableLineItem>) =>
        setLineItems((prev) => prev.map((li) => {
            if (li.id !== id) return li;
            const next = { ...li, ...patch };
            // Same markup rule the quote uses, applied at the point the cost is
            // typed, so the sell price a mechanic sees here is the one the
            // customer will see later.
            if (patch.costPrice !== undefined && next.type === 'part') {
                next.unitPrice = sellPrice(patch.costPrice);
            }
            return next;
        }));
    const removeLineItem = (id: string) => setLineItems((prev) => prev.filter((li) => li.id !== id));

    const handlePhoto = async (findingId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !activeOrgId) return;
        setUploadingId(findingId);
        try {
            const urls: string[] = [];
            for (const file of Array.from(files)) {
                const ext = file.name.split('.').pop();
                const path = `${activeOrgId}/${reportId}/${findingId}/${Date.now()}.${ext}`;
                const { error: upErr } = await supabase.storage
                    .from('diagnostic-photos')
                    .upload(path, file, { upsert: true });
                if (upErr) {
                    console.error('Diagnostic photo upload failed', upErr);
                    continue;
                }
                // `diagnostic-photos` is a private bucket — these are customer
                // vehicle photographs (personal data, often with number plates).
                // A public URL would serve them with no auth and bypass the
                // bucket's org-scoped RLS policies entirely, so sign instead.
                const { data, error: signErr } = await supabase.storage
                    .from('diagnostic-photos')
                    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
                if (signErr) {
                    console.error('Diagnostic photo signing failed', signErr);
                    continue;
                }
                if (data?.signedUrl) urls.push(data.signedUrl);
            }
            if (urls.length) {
                setFindings((prev) =>
                    prev.map((f) => (f.id === findingId ? { ...f, photos: [...f.photos, ...urls] } : f)),
                );
            }
        } finally {
            setUploadingId(null);
        }
    };

    const selectCustomer = (c: CustomerSearchResult) => {
        setPickerCustomer({ id: c.id, name: c.full_name });
    };

    const chooseVehicle = (v: VehicleRow) => {
        if (!pickerCustomer) return;
        setCustomerId(pickerCustomer.id);
        setCustomerName(pickerCustomer.name);
        setVehicleId(v.id);
        setVehicleVrm(v.vrm);
        setVehicleDesc([v.make, v.model].filter(Boolean).join(' '));
        setPickerDone(true);
    };

    const resetPicker = () => {
        setPickerDone(false);
        setPickerCustomer(null);
        setCustomerId(undefined);
        setCustomerName('');
        setVehicleId(undefined);
        setVehicleVrm('');
        setVehicleDesc('');
        setForm(blankForm()); setVehicleConfirmed(false);
    };

    // Create a brand-new customer + vehicle (walk-in) via the app's canonical
    // atomic RPC — the same one the CRM's "Add Customer" uses — so it shows up
    // in the CRM properly.
    const createNewCustomer = async () => {
        if (!activeOrgId || !form.name.trim() || !form.vrm.trim()) return;
        setSavingCustomer(true);
        setError(null);
        try {
            const { data, error: rpcErr } = await supabase.rpc('fn_create_customer_and_vehicle', {
                p_org_id: activeOrgId,
                p_full_name: form.name.trim(),
                p_phone: form.phone.trim() || null,
                // Was hardcoded null. Every walk-in added mid-diagnostic
                // therefore had no email — which is why the Email button
                // disappeared from their client page, why their quote could not
                // be sent, and why the whole flow dead-ended at the first point
                // it needed to reach the customer.
                p_email: form.email.trim() || null,
                p_address: null,
                p_vrm: form.vrm.trim().toUpperCase() || null,
                p_make: form.make.trim() || null,
                p_model: form.model.trim() || null,
                // Carried across from the reg lookup. These used to be dropped on
                // the floor here, so a vehicle created from a diagnostic arrived
                // in the CRM with only make and model and had to be completed by
                // hand — and send-mot-reminders had no expiry date to work from.
                p_year: form.year ? parseInt(form.year, 10) : null,
                p_engine: form.engine.trim() || null,
                p_mileage: form.mileage ? parseInt(form.mileage, 10) : null,
                p_colour: form.colour.trim() || null,
                p_fuel_type: form.fuel_type.trim() || null,
                p_mot_expiry: form.mot_expiry || null,
            });
            if (rpcErr) throw rpcErr;
            const res = data as { customer_id?: string; vehicle_id?: string } | null;
            setCustomerId(res?.customer_id);
            setCustomerName(form.name.trim());
            setVehicleId(res?.vehicle_id);
            // MOT history for the vehicle this just created, so the customer's
            // profile is complete the moment they open it rather than after
            // someone presses Refresh MOT. Bounded; see refreshMotHistoryWithin.
            if (res?.vehicle_id) void refreshMotHistoryWithin(res.vehicle_id);
            setVehicleVrm(form.vrm.trim().toUpperCase());
            setVehicleDesc([form.make.trim(), form.model.trim()].filter(Boolean).join(' '));
            setPickerDone(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not create customer');
        } finally {
            setSavingCustomer(false);
        }
    };

    // Add a new vehicle to the already-selected existing customer.
    const createVehicleForCustomer = async () => {
        if (!activeOrgId || !pickerCustomer || !form.vrm.trim()) return;
        setSavingCustomer(true);
        setError(null);
        try {
            const clean = form.vrm.trim().toUpperCase().replace(/\s+/g, '');
            const { data, error: insErr } = await supabase
                .from('vehicles')
                .insert({
                    org_id: activeOrgId,
                    owner_id: pickerCustomer.id,
                    vrm: clean,
                    make: form.make.trim() || null,
                    model: form.model.trim() || null,
                    // Same as above: keep everything the reg lookup returned
                    // rather than storing three fields and discarding the rest.
                    year: form.year ? parseInt(form.year, 10) : null,
                    engine: form.engine.trim() || null,
                    mileage: form.mileage ? parseInt(form.mileage, 10) : null,
                    colour: form.colour.trim() || null,
                    fuel_type: form.fuel_type.trim() || null,
                    mot_expiry: form.mot_expiry || null,
                })
                .select('id, vrm, make, model')
                .single();
            if (insErr) throw insErr;
            const createdVehicle = data as VehicleRow;
            if (createdVehicle?.id) void refreshMotHistoryWithin(createdVehicle.id);
            chooseVehicle(createdVehicle);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not add vehicle');
        } finally {
            setSavingCustomer(false);
        }
    };

    /**
     * The customer and vehicle this report belongs to.
     *
     * Started from an appointment, `customerId` / `vehicleId` are never set in
     * state — the ids live on the appointment row. This used to be resolved
     * inside persist() ONLY, so the report filed itself correctly but
     * handleGenerateQuote still read the empty state, skipped the vehicle
     * lookup entirely, and fell back to splitting the free-text description.
     * That is why a quote generated from an appointment-led diagnostic arrived
     * with no colour, year, engine, fuel type or mileage — the app had the
     * vehicle record the whole time and never asked for it.
     *
     * Both callers now go through here, so there is one answer.
     */
    const resolveLinkedIds = async (): Promise<{ customerId?: string; vehicleId?: string }> => {
        let linkedCustomerId = customerId;
        let linkedVehicleId = vehicleId;
        if ((!linkedCustomerId || !linkedVehicleId) && context?.appointmentId) {
            const { data: appt, error: apptError } = await supabase
                .from('appointments')
                .select('customer_id, vehicle_id')
                .eq('id', context.appointmentId)
                .maybeSingle();
            if (apptError) reportError(apptError, 'diagnostic-modal.appointments');
            linkedCustomerId = linkedCustomerId ?? (appt?.customer_id as string | undefined) ?? undefined;
            linkedVehicleId = linkedVehicleId ?? (appt?.vehicle_id as string | undefined) ?? undefined;
        }
        return { customerId: linkedCustomerId, vehicleId: linkedVehicleId };
    };

    const persist = async (status: 'in_progress' | 'complete' | 'quoted', quoteId?: string) => {
        if (!activeOrgId) throw new Error('No active organisation');

        const { customerId: linkedCustomerId, vehicleId: linkedVehicleId } = await resolveLinkedIds();

        const row = {
            id: reportId,
            org_id: activeOrgId,
            customer_id: linkedCustomerId ?? null,
            vehicle_id: linkedVehicleId ?? null,
            appointment_id: context?.appointmentId ?? null,
            customer_name: customerName || null,
            vehicle_vrm: vehicleVrm || null,
            vehicle_desc: vehicleDesc || null,
            summary: summary || null,
            findings: validFindings,
            line_items: lineItems.filter((li) => li.description.trim() || li.unitPrice > 0),
            status,
            ...(quoteId ? { quote_id: quoteId } : {}),
        };
        const { error: upErr } = await supabase
            .from('diagnostic_reports')
            .upsert(row, { onConflict: 'id' });
        if (upErr) throw upErr;
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            // A report that's already been quoted keeps that status when edited.
            await persist(context?.status === 'quoted' ? 'quoted' : 'complete');
            onSaved?.();
            // Editing a saved report drops back to the read-only view so the
            // mechanic can check what they just wrote; a new one closes out.
            if (context?.reportId) setMode('view');
            else onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save report');
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateQuote = async () => {
        if (!activeOrgId || validFindings.length === 0) return;
        setGenerating(true);
        setError(null);
        try {
            // Build quote line items from findings: a part line for the suggested
            // price and a labour line for the suggested hours (priced at the org's
            // default labour rate). Mirrors QuoteBuilder's metadata shape exactly.
            // A report saved with real lines converts by COPYING them. The
            // derivation below is now only the legacy path, for reports written
            // before diagnostic_reports.line_items existed.
            const explicitLines = lineItems
                .filter((li) => li.description.trim() || li.unitPrice > 0)
                .map((li) => ({
                    id: li.id, type: li.type, description: li.description.trim(),
                    quantity: li.quantity || 1, unitPrice: li.unitPrice || 0,
                    costPrice: li.costPrice ?? 0,
                }));

            const derivedLines = validFindings.flatMap((f) => {
                const items: Array<{
                    id: string;
                    type: 'part' | 'labour';
                    description: string;
                    quantity: number;
                    unitPrice: number;
                    costPrice?: number;
                }> = [];
                // The PART goes in the part line, not the fault. `partDescription`
                // falls back to the title so reports written before that field
                // existed behave exactly as they did.
                const partName = (f.partDescription || '').trim() || f.title;
                if (f.price > 0) {
                    // cost → quantity → sell → total, the same columns the quote
                    // builder shows. Carrying costPrice across is what lets the
                    // quote keep showing margin instead of forgetting it the
                    // moment the line arrived from a diagnostic.
                    items.push({
                        id: uid(),
                        type: 'part',
                        description: partName,
                        quantity: 1,
                        costPrice: f.price,
                        unitPrice: sellPrice(f.price),
                    });
                }
                if (f.labourHours > 0) {
                    items.push({
                        id: uid(),
                        type: 'labour',
                        description: `Labour — ${partName}`,
                        quantity: f.labourHours,
                        unitPrice: labourRate,
                    });
                }
                return items;
            });

            const quoteLines = explicitLines.length > 0 ? explicitLines : derivedLines;
            const totalAmount = quoteLines.reduce((s, i) => s + i.quantity * i.unitPrice, 0);

            // Carry the real records across, rather than the blanks this used to
            // write. The report is already linked to a customer and a vehicle, so
            // every one of these fields is a lookup away — hardcoding '' meant the
            // user re-typed phone, email, colour, year, engine and fuel type on a
            // quote for a vehicle the app had just finished inspecting. Make and
            // model were split out of a free-text description ("BMW 3 Series
            // 320d" → make "BMW", model "3 Series 320d"), which was a guess even
            // when it happened to look right.
            const { customerId: linkedCustomerId, vehicleId: linkedVehicleId } = await resolveLinkedIds();
            const [contactResult, vehicleResult] = await Promise.all([
                linkedCustomerId
                    ? supabase.from('customers_decrypted').select('full_name, email, phone').eq('id', linkedCustomerId).maybeSingle()
                    : Promise.resolve({ data: null }),
                linkedVehicleId
                    ? supabase.from('vehicles').select('vrm, make, model, colour, mileage, year, engine, fuel_type, mot_last_odometer_value, mot_last_odometer_unit, mot_last_test_date').eq('id', linkedVehicleId).maybeSingle()
                    : Promise.resolve({ data: null }),
            ]);

            const contact = contactResult.data;
            const vehicle = vehicleResult.data;

            // Fall back to the free-text split only when there is no vehicle
            // record to read — an unsaved walk-in still gets the old behaviour
            // rather than an empty quote.
            const [vMake, ...vRest] = (vehicleDesc || '').split(' ');
            const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
            // Recorded mileage if there is one, else the last MOT reading,
            // carried with its source so the quote prints "at last MOT, <date>"
            // (the same rule as QuoteBuilder's mileageMeta).
            const motReading = vehicle ? motOdometerOffer(vehicle) : null;
            const quoteMileage = vehicle?.mileage != null && vehicle.mileage !== ''
                ? { mileage: str(vehicle.mileage), mileageSource: 'recorded' as const }
                : motReading
                  ? { mileage: String(motReading.miles), mileageSource: 'mot' as const, mileageMotDate: motReading.testDate }
                  : { mileage: '' };

            const metadata = {
                customer: {
                    name: contact?.full_name || customerName,
                    phone: str(contact?.phone),
                    email: str(contact?.email),
                },
                vehicle: {
                    vrm: vehicle?.vrm || vehicleVrm,
                    make: vehicle?.make || vMake || '',
                    model: vehicle?.model || vRest.join(' '),
                    colour: str(vehicle?.colour),
                    ...quoteMileage,
                    year: str(vehicle?.year),
                    engine: str(vehicle?.engine),
                    fuelType: str(vehicle?.fuel_type),
                },
                lineItems: quoteLines,
                // The findings ARE the explanation of the work; the summary is
                // the mechanic's own wording. Both, in that order, separated —
                // previously whichever one existed was dropped in raw and the
                // quote's notes box read as one run-on paragraph.
                notes: [summary.trim(), diagnosticNotes(validFindings)].filter(Boolean).join('\n\n'),
            };

            const { data, error: qErr } = await supabase
                .from('quotes')
                // customer_id is what lets "Convert to job" file the job against
                // this customer instead of re-matching by contact details,
                // which minted a duplicate whenever those were missing.
                .insert({ org_id: activeOrgId, metadata, total_amount: totalAmount, status: 'draft', customer_id: linkedCustomerId ?? null })
                .select('id')
                .single();
            if (qErr) throw qErr;

            await persist('quoted', data.id);
            onSaved?.();
            onClose();
            navigate(`/workshop?tab=quotes&quote=${data.id}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to generate quote');
        } finally {
            setGenerating(false);
        }
    };

    // ── Customer/vehicle picker (walk-in, no context) ────────────────────────
    const dInputCls =
        'w-full h-11 px-3 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold focus:shadow-focus transition-all placeholder:text-fg-4';

    if (!pickerDone) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title="New Diagnostic"
                subtitle="Choose the customer and vehicle, then record your findings" editor
                footer={<div className="flex flex-col sm:items-end gap-2">
                    <button type="button" onClick={pickerCustomer ? createVehicleForCustomer : createNewCustomer}
                        disabled={savingCustomer || !(pickerCustomer || form.name.trim()) || !form.vrm.trim()}
                        className="fd-btn fd-btn-go fd-btn-lg w-full sm:w-auto">
                        {savingCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                        Continue to findings
                    </button>
                    <p className="text-xs text-fg-3">An existing vehicle opens its diagnostic directly.</p>
                </div>}
            >
                <div className="space-y-6">
                    {error && <p role="alert" className="p-3 rounded-lg bg-danger/10 text-danger text-sm">{error}</p>}
                    <VRMLookup onVehicleConfirmed={handleVehicleConfirmed}
                        onVehicleCleared={() => {
                            setForm(prev => ({ ...blankForm(), name: prev.name, phone: prev.phone, email: prev.email }));
                            confirmedLookup.current = null;
                            setVehicleConfirmed(false);
                            setVehicleId(undefined);
                            setVehicleVrm('');
                            setVehicleDesc('');
                        }}
                        onCustomerFound={pickerCustomer ? undefined : handleCustomerFound}
                        onSkipToManual={handleSkipToManual} confirmButtonLabel="Apply Vehicle to Diagnostic" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <section className="min-w-0 bg-tint/5 rounded-lg p-4 space-y-4">
                            <h3 className="text-sm font-bold text-fg">Customer Details</h3>
                            {pickerCustomer ? <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-fg">{pickerCustomer.name}</span>
                                <button type="button" className="fd-btn" onClick={() => setPickerCustomer(null)}>Change</button>
                            </div> : <>
                                <CustomerTypeahead onSelect={selectCustomer} placeholder="Search existing customers…" />
                                <label className="block"><span className={dFieldLabelCls}>Full name *</span><input value={form.name} onChange={e => setF({ name: e.target.value })} className={dInputCls} /></label>
                                <label className="block"><span className={dFieldLabelCls}>Phone</span><input type="tel" value={form.phone} onChange={e => setF({ phone: e.target.value })} className={dInputCls} /></label>
                                <label className="block"><span className={dFieldLabelCls}>Email</span><input type="email" value={form.email} onChange={e => setF({ email: e.target.value })} className={dInputCls} /></label>
                            </>}
                            {pickerCustomer && customerVehicles.length > 0 && <div className="space-y-2">
                                <p className="text-xs text-fg-3">Choose a vehicle already on file</p>
                                {customerVehicles.map(v => <button key={v.id} type="button" onClick={() => chooseVehicle(v)} className="w-full flex flex-wrap items-center gap-2 p-3 rounded-lg border border-line text-left hover:bg-tint/5">
                                    <span className="fd-plate">{v.vrm}</span><span className="text-sm text-fg-2">{[v.make, v.model].filter(Boolean).join(' ')}</span>
                                </button>)}
                            </div>}
                        </section>
                        <section className="min-w-0 bg-tint/5 rounded-lg p-4 space-y-4">
                            <h3 className="text-sm font-bold text-fg flex items-center gap-2"><Car className="w-4 h-4" /> Vehicle Details {vehicleConfirmed && <span className="text-xs text-success">Applied</span>}</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {([['vrm','Registration'],['make','Make'],['model','Model'],['year','Year'],['colour','Colour'],['mileage','Mileage'],['engine','Engine'],['fuel_type','Fuel type']] as const).map(([key,label]) =>
                                    <label key={key} className="min-w-0 block"><span className={dFieldLabelCls}>{label}</span><input value={form[key]} onChange={e => setF({ [key]: e.target.value })} className={dInputCls} /></label>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </Modal>
        );
    }

    // ── Saved report, read-only ──────────────────────────────────────────────
    if (mode === 'view') {
        // Marked up, because this is the figure the customer is quoted. Summing
        // raw cost here would show the mechanic a total the quote then contradicts.
        const estimate = validFindings.reduce(
            (sum, f) => sum + sellPrice(f.price || 0) + (f.labourHours || 0) * labourRate,
            0,
        );
        const reportDate = context?.createdAt ? new Date(context.createdAt) : null;

        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title="Diagnostic report"
                subtitle={[customerName, vehicleVrm].filter(Boolean).join(' · ') || 'Saved report'}
                icon={Stethoscope}
                editor
                footer={
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={() => setMode('edit')}
                            className="fd-btn fd-btn-secondary sm:flex-1"
                        >
                            <Pencil className="w-4 h-4" />
                            Edit report
                        </button>
                        {context?.quoteId ? (
                            <button
                                onClick={() => {
                                    onClose();
                                    navigate(`/workshop?tab=quotes&quote=${context.quoteId}`);
                                }}
                                className="sm:flex-1 shrink-0 min-h-11 h-11 inline-flex items-center justify-center gap-2 rounded-lg fd-btn-primary text-sm font-semibold font-display transition-colors"
                            >
                                <FileText className="w-4 h-4" />
                                View quote
                            </button>
                        ) : (
                            <button
                                onClick={handleGenerateQuote}
                                disabled={generating || validFindings.length === 0}
                                className="sm:flex-1 shrink-0 min-h-11 h-11 inline-flex items-center justify-center gap-2 rounded-lg fd-btn-primary text-sm font-semibold font-display transition-colors disabled:opacity-50"
                            >
                                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                Generate quote
                            </button>
                        )}
                    </div>
                }
            >
                <div className="space-y-4">
                    {error && (
                        <div className="p-3 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                            {error}
                        </div>
                    )}

                    {/* Vehicle + date strip */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fg-3">
                        {vehicleVrm && (
                            <span
                                className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-fd-sm-tight font-bold tracking-wider"
                                style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                            >
                                {vehicleVrm}
                            </span>
                        )}
                        {vehicleDesc && <span className="text-fg">{vehicleDesc}</span>}
                        {reportDate && (
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" />
                                {reportDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                        )}
                        {context?.status && (
                            <span className="uppercase tracking-wider font-display font-semibold text-fd-3xs text-fg-4">
                                {context.status === 'in_progress' ? 'In progress' : context.status === 'quoted' ? 'Quoted' : 'Complete'}
                            </span>
                        )}
                    </div>

                    {summary && (
                        <div className="rounded-lg bg-tint/5 p-4">
                            <p className="text-fd-sm-tight uppercase tracking-[0.16em] font-display font-semibold text-fg-3 mb-1.5">
                                Summary for the customer
                            </p>
                            <p className="text-sm text-fg whitespace-pre-wrap">{summary}</p>
                        </div>
                    )}

                    {validFindings.length === 0 ? (
                        <p className="text-sm text-fg-3 py-6 text-center">
                            No findings were recorded on this report.
                        </p>
                    ) : (
                        validFindings.map((f) => {
                            const meta = SEVERITY_META[f.severity];
                            const line = sellPrice(f.price || 0) + (f.labourHours || 0) * labourRate;
                            return (
                                <div
                                    key={f.id}
                                    className="rounded-lg bg-tint/5 p-4 space-y-2"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-fd-sm-tight uppercase tracking-wider font-display text-fg-3">
                                                {f.system}
                                            </p>
                                            <p className="text-sm font-medium text-fg break-words">{f.title}</p>
                                        </div>
                                        <span
                                            className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-fd-3xs font-bold uppercase tracking-wider ${meta.className}`}
                                        >
                                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                            {meta.label}
                                        </span>
                                    </div>

                                    {f.notes && (
                                        <p className="text-sm text-fg-3 whitespace-pre-wrap">{f.notes}</p>
                                    )}

                                    {(f.price > 0 || f.labourHours > 0) && (
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-3 font-mono">
                                            {f.price > 0 && <span>Parts £{f.price.toFixed(2)}</span>}
                                            {f.labourHours > 0 && (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Wrench className="w-3.5 h-3.5" />
                                                    {f.labourHours}h
                                                    {labourRate > 0 && ` @ £${labourRate.toFixed(2)}`}
                                                </span>
                                            )}
                                            {line > 0 && (
                                                <span className="text-fg">£{line.toFixed(2)}</span>
                                            )}
                                        </div>
                                    )}

                                    {f.photos.length > 0 && (
                                        <div className="flex items-center gap-2 flex-wrap pt-1">
                                            {f.photos.map((url) => (
                                                <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="w-16 h-16 rounded-lg overflow-hidden border border-line hover:border-gold transition-colors"
                                                >
                                                    <img src={url} alt="finding" className="w-full h-full object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}

                    {estimate > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-line">
                            <span className="text-fd-sm-tight uppercase tracking-[0.16em] font-display font-semibold text-fg-3">
                                Suggested work (ex VAT)
                            </span>
                            <span className="font-mono font-semibold text-fg">£{estimate.toFixed(2)}</span>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }

    // ── Findings capture ─────────────────────────────────────────────────────
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={context?.reportId ? 'Edit diagnostic' : 'Diagnostic'}
            subtitle={[customerName, vehicleVrm].filter(Boolean).join(' · ')}
            icon={Stethoscope}
            editor
            footer={
                <div className="flex flex-col sm:flex-row gap-2">
                    <button
                        onClick={handleSave}
                        disabled={saving || generating || validFindings.length === 0}
                        className="fd-btn fd-btn-secondary sm:flex-1"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {context?.reportId ? 'Save changes' : 'Save report'}
                    </button>
                    <button
                        onClick={handleGenerateQuote}
                        disabled={saving || generating || validFindings.length === 0}
                        className="fd-btn fd-btn-go sm:flex-1"
                    >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate quote
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                {error && (
                    <div className="p-3 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                        {error}
                    </div>
                )}

                {findings.map((f, idx) => (
                    <div
                        key={f.id}
                        className="rounded-lg bg-tint/5 p-4 space-y-3"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-fd-sm-tight uppercase tracking-[0.16em] font-display font-semibold text-fg-3">
                                Finding {idx + 1}
                            </span>
                            {findings.length > 1 && (
                                <button
                                    onClick={() => removeFinding(f.id)}
                                    className="p-1.5 rounded-md text-fg-3 hover:text-danger hover:bg-danger/10 transition-colors"
                                    aria-label="Remove finding"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* System + severity.
                            Every field here carries a visible label. They were
                            briefly bare — placeholders had been used as labels,
                            and removing the placeholders left nothing at all, so
                            you could not tell the title box from the notes box. */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="block min-w-0">
                                <span className={dFieldLabelCls}>Area of the vehicle</span>
                                <select
                                    value={f.system}
                                    onChange={(e) => updateFinding(f.id, { system: e.target.value })}
                                    className="mt-1 w-full h-11 px-3 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold transition-colors"
                                >
                                    {SYSTEMS.map((s) => (
                                        <option key={s} value={s} className="bg-panel">
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div className="min-w-0">
                                <span className={dFieldLabelCls}>How urgent is it?</span>
                                <div className="mt-1 flex gap-1.5">
                                    {(Object.keys(SEVERITY_META) as Severity[]).map((sev) => {
                                        const meta = SEVERITY_META[sev];
                                        const active = f.severity === sev;
                                        return (
                                            <button
                                                key={sev}
                                                onClick={() => updateFinding(f.id, {
                                                    severity: sev,
                                                    // Re-base the reminder unless the mechanic set
                                                    // their own — critical drops it entirely, since
                                                    // a critical fault is a job, not a follow-up.
                                                    ...(f.remindAt === defaultRemindAt(f.severity) || !f.remindAt
                                                        ? { remindAt: sev === 'critical' ? undefined : defaultRemindAt(sev) }
                                                        : sev === 'critical' ? { remindAt: undefined } : {}),
                                                })}
                                                className={`flex-1 h-11 rounded-lg border text-xs font-semibold font-display transition-colors inline-flex items-center justify-center gap-1.5 ${
                                                    active
                                                        ? meta.className
                                                        : 'bg-card border-line text-fg-3 hover:text-fg'
                                                }`}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                                {meta.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* What is wrong — the one line the customer reads. */}
                        <label className="block">
                            <span className={dFieldLabelCls}>What is wrong</span>
                            <span className="block text-fd-3xs text-fg-4 mt-0.5">
                                One short line — this is what the customer sees.
                            </span>
                            <input
                                value={f.title}
                                onChange={(e) => updateFinding(f.id, { title: e.target.value })}
                                className="mt-1 w-full h-11 px-3 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold transition-colors"
                            />
                        </label>

                        <details className="rounded-lg border border-line p-3" open={Boolean(f.notes || f.photos.length)}>
                            <summary className="cursor-pointer text-sm font-medium text-fg-2 min-h-11 flex items-center">Notes, photos & follow-up</summary>
                            <div className="mt-3 space-y-4">
                        <label className="block">
                            <span className={dFieldLabelCls}>Your notes (optional)</span>
                            <span className="block text-fd-3xs text-fg-4 mt-0.5">
                                Measurements, readings, anything you want on the record.
                            </span>
                            <textarea
                                value={f.notes}
                                onChange={(e) => updateFinding(f.id, { notes: e.target.value })}
                                rows={2}
                                className="mt-1 w-full px-3 py-2 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold transition-colors resize-none"
                            />
                        </label>

                        {/* Bring it back later.
                            The whole reason the follow-ups lane can exist: the
                            mechanic already knows the pads will need doing in a
                            few months, and used to have nowhere to put that. It
                            is pre-filled and optional — never a field you must
                            fill in to save a report. */}
                        {f.severity !== 'critical' && (
                            <label className="block">
                                <span className={dFieldLabelCls}>Remind me around</span>
                                <span className="block text-fd-3xs text-fg-4 mt-0.5">
                                    It comes back on your follow-ups list on this date.
                                </span>
                                <div className="mt-1 flex items-center gap-2">
                                    <input
                                        type="date"
                                        value={f.remindAt ?? ''}
                                        onChange={(e) => updateFinding(f.id, { remindAt: e.target.value || undefined })}
                                        className="flex-1 min-w-0 h-11 px-3 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold transition-colors"
                                    />
                                    {f.remindAt && (
                                        <button
                                            type="button"
                                            onClick={() => updateFinding(f.id, { remindAt: undefined })}
                                            className="shrink-0 h-11 px-3 rounded-lg text-xs font-semibold text-fg-3 hover:text-fg border border-line transition-colors"
                                        >
                                            Don't remind
                                        </button>
                                    )}
                                </div>
                            </label>
                        )}

                        {/* No money on a finding.
                            A finding is a FAULT: what is wrong, how serious, and
                            the evidence. What it costs to put right is a parts
                            and labour line, entered once below in the same editor
                            the quote and job use. Having a "parts cost" and
                            "labour hours" here as well meant two places to type a
                            price, neither of which looked like the quote the
                            customer eventually saw. The fields remain on the type
                            so reports saved before this still convert. */}

                        {/* Photos */}
                        <div>
                            <span className={dFieldLabelCls}>Photos (optional)</span>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                {f.photos.map((url) => (
                                    <div key={url} className="relative w-14 h-14 rounded-lg overflow-hidden border border-line">
                                        <img src={url} alt="finding" className="w-full h-full object-cover" />
                                        <button
                                            onClick={() => updateFinding(f.id, { photos: f.photos.filter((p) => p !== url) })}
                                            className="absolute top-0 right-0 bg-scrim/60 text-fg p-0.5 rounded-bl"
                                            aria-label="Remove photo"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                <label className="w-14 h-14 rounded-lg border border-dashed border-line hover:border-gold grid place-items-center cursor-pointer transition-colors text-fg-3 hover:text-gold">
                                    {uploadingId === f.id ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Camera className="w-5 h-5" />
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handlePhoto(f.id, e)}
                                    />
                                </label>
                            </div>
                        </div>
                            </div>
                        </details>
                    </div>
                ))}

                <button
                    onClick={() => setFindings((prev) => [...prev, blankFinding()])}
                    className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-line hover:border-gold hover:text-gold text-fg-3 text-sm font-semibold font-display transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add another finding
                </button>

                {/* Parts & labour — the SAME editor the quote and job screens
                    use. Money used to be two numbers tucked inside each finding,
                    which is why it was invisible and why converting had to invent
                    line items from the fault text. Filled in here, the quote is a
                    straight copy. */}
                <div className="rounded-lg bg-tint/5 p-4">
                    <LineItemsEditor
                        items={lineItems}
                        onUpdate={updateLineItem}
                        onRemove={removeLineItem}
                        onAdd={addLineItem}
                        partsMarkup={partsMarkup}
                        subtotal={lineItems.reduce((sum, li) => sum + (li.quantity || 0) * (li.unitPrice || 0), 0)}
                    />
                </div>

                {/* Summary */}
                <div>
                    <label className="block text-fd-sm-tight uppercase tracking-[0.16em] text-fg-3 font-display font-semibold">
                        Summary for the customer (optional)
                    </label>
                    <p className="text-fd-3xs text-fg-4 mt-0.5 mb-2">
                        A plain-English sentence or two that goes on the report and the email.
                    </p>
                    <textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-sm text-fg outline-none focus:border-gold transition-colors resize-none placeholder:text-fg-4"
                    />
                </div>

                {/* Back to the saved report without saving. */}
                {context?.reportId && (
                    <button
                        onClick={() => setMode('view')}
                        className="inline-flex items-center gap-1.5 text-xs text-fg-3 hover:text-gold transition-colors"
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        Back to report
                    </button>
                )}

                {/* Change vehicle (walk-in only — not when opened from an appointment) */}
                {!context?.customerName && !context?.reportId && (
                    <button
                        onClick={resetPicker}
                        className="inline-flex items-center gap-1.5 text-xs text-fg-3 hover:text-gold transition-colors"
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        Change customer / vehicle
                    </button>
                )}
            </div>
        </Modal>
    );
};

// Reads as prose, not as a template. The old form was
// `• [Critical] Engine: …`, and the square-bracketed tag looked exactly like
// leftover placeholder text sitting in the quote's notes box.
function diagnosticNotes(findings: Finding[]): string {
    if (findings.length === 0) return '';
    const lines = findings.map(
        (f) => `• ${f.system} (${SEVERITY_META[f.severity].label.toLowerCase()}): ${f.title}${f.notes ? ` — ${f.notes}` : ''}`,
    );
    return ['From the diagnostic:', ...lines].join('\n');
}
